import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchAssignedSearchKeyTerms } from "../../app/actions/searchKeyTermActions";
import {
  FLOW_MODULE_DEFINITIONS,
  getModuleDisplayLabel,
  isFlowEngineEnabledForProcess
} from "../../app/helpers/flowEngineConfigHelper";
import { MODULE_FICHA_NAV, buildFichaNavState, readFichaReturnContext } from "../../app/helpers/moduleFichaNavHelper";
import { setPreDriCreateActive, setPreDriTermId } from "../../app/helpers/preDriTermLinkHelper";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";

const MODULE_ORDER = Object.keys(FLOW_MODULE_DEFINITIONS);

const PreDriCreateBar = ({ process, title }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { roleKey } = useActiveSpecialistRole();
  const configuration = useSelector((state) => state.configuration);
  const { roleLogin, roleSelected } = useSelector((state) => state.userRole);
  const boxRef = useRef(null);
  const [terms, setTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const stored = readFichaReturnContext(process);
  const returnTo = location.state?.returnTo || stored.returnTo || "";
  const fromPreTray = String(returnTo).includes("specialist-pre-manager");
  const show = roleKey === "pre-manager" || fromPreTray;
  const moduleId = FLOW_MODULE_DEFINITIONS[process]?.moduleId;

  const modules = useMemo(() => {
    const access = roleSelected?.moduleAccess || roleLogin?.moduleAccess || [];
    const allowed = Array.isArray(access)
      ? access.map((item) => String(item).toLowerCase())
      : [];
    const source = allowed.length ? allowed : MODULE_ORDER;
    const unique = [];
    source.forEach((key) => {
      if (!FLOW_MODULE_DEFINITIONS[key] || unique.includes(key)) {
        return;
      }
      if (!isFlowEngineEnabledForProcess(configuration, key) && key !== process) {
        return;
      }
      unique.push(key);
    });
    if (process && !unique.includes(process)) {
      unique.unshift(process);
    }
    return unique;
  }, [configuration, process, roleLogin, roleSelected]);

  useEffect(() => {
    setPreDriCreateActive(show);
    setPreDriTermId(null);
    setSelectedLabel("");
    setQuery("");
    return () => setPreDriCreateActive(false);
  }, [show, process]);

  useEffect(() => {
    if (!show || !moduleId) {
      setTerms([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingTerms(true);
    dispatch(fetchAssignedSearchKeyTerms(moduleId)).then((rows) => {
      if (!cancelled) {
        setTerms(Array.isArray(rows) ? rows : []);
        setLoadingTerms(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch, moduleId, show]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const visibleTerms = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || needle === selectedLabel.trim().toLowerCase()) {
      return terms;
    }
    return terms.filter((row) => String(row.option_label || row.title || row.id).toLowerCase().includes(needle));
  }, [query, selectedLabel, terms]);

  const handleModuleChange = (event) => {
    const next = event.target.value;
    if (!next || next === process) {
      return;
    }
    const nav = MODULE_FICHA_NAV[next];
    if (!nav?.newPath) {
      return;
    }
    setPreDriTermId(null);
    navigate(nav.newPath, {
      state: buildFichaNavState(next, {
        returnTo: returnTo || "/specialist-pre-manager-selection",
        returnLabelKey: location.state?.returnLabelKey || stored.returnLabelKey || "specialist.selection.pre-manager.title",
        returnLabel: location.state?.returnLabel || stored.returnLabel || ""
      })
    });
  };

  const pickTerm = (row) => {
    const label = row.option_label || `#${row.id}`;
    setPreDriTermId(row.id);
    setSelectedLabel(label);
    setQuery(label);
    setOpen(false);
  };

  if (!show) {
    return <div className="d-flex justify-content-center page-title">{title}</div>;
  }

  return (
    <>
      <div className="container-fluid px-3 pt-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-4 col-lg-3">
            <label className="form-label mb-1" htmlFor={`pre-dri-module-${process}`}>
              {t("specialist.selection.dri.module", "Módulo")} *
            </label>
            <select
              id={`pre-dri-module-${process}`}
              className="form-select"
              value={process}
              onChange={handleModuleChange}
            >
              {modules.map((key) => (
                <option key={key} value={key}>
                  {getModuleDisplayLabel(key, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-8 col-lg-6 position-relative" ref={boxRef}>
            <label className="form-label mb-1" htmlFor={`pre-dri-term-${process}`}>
              {t("specialist.selection.dri.term", "Término clave")} *
            </label>
            <input
              id={`pre-dri-term-${process}`}
              className="form-control"
              autoComplete="off"
              value={query}
              placeholder={loadingTerms
                ? t("specialist.selection.dri.term-loading", "Cargando términos clave...")
                : t("specialist.selection.dri.term-placeholder", "Escribe para filtrar los términos clave")}
              onFocus={() => setOpen(true)}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelectedLabel("");
                setPreDriTermId(null);
                setOpen(true);
              }}
            />
            {open && (
              <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 20, maxHeight: 280, overflowY: "auto" }}>
                {visibleTerms.length === 0 && (
                  <div className="list-group-item small text-muted">
                    {t(
                      "specialist.selection.dri.term-empty",
                      "No hay términos clave de este módulo asignados a su usuario."
                    )}
                  </div>
                )}
                {visibleTerms.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    className="list-group-item list-group-item-action small text-start"
                    onClick={() => pickTerm(row)}
                  >
                    {row.option_label || `#${row.id}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center page-title">{title}</div>
    </>
  );
};

export default PreDriCreateBar;
